import _ from 'lodash'
import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { toast, ToastContainer } from 'react-toastify'
import SqaureLoader from '../../../components/Loader/SqaureLoader'
import ExportToExcel from '../../../components/exportExcel/ExportToExcel'
import { getTravailVisites, getTravailVisiteUsers } from '../../../services/Rapports/TravailVisiteApi'
import TravailVisiteDetail from './TravailVisiteDetail'
import { SECTION_TITLES, colorForRate, formatPct, pct } from './travailVisiteConstants'
import '../RapportVisite/rapportVisite.css'
import './travailVisite.css'

export default function RapportTravailVisite() {
    const [t] = useTranslation('global')

    // Valeurs initiales lues dans l'URL (lien du tableau de bord) :
    // /rapports/travailVisite&datedebut=...&datefin=...&user_code=...
    const params = useParams()
    const dateValide = (d) => (d && moment(d, 'YYYY-MM-DD', true).isValid() ? d : null)

    // ---- Filtres ----
    const [datedebut, setdatedebut] = useState(dateValide(params.datedebut) || moment().startOf('month').format('YYYY-MM-DD'))
    const [datefin, setdatefin] = useState(dateValide(params.datefin) || moment().format('YYYY-MM-DD'))
    const [userCode, setuserCode] = useState(params.user_code || 'all')
    const [userOptions, setuserOptions] = useState([])
    const [search, setsearch] = useState('')

    // ---- Données ----
    const [loading, setloading] = useState(true)
    const [stats, setstats] = useState({})
    const [actions, setactions] = useState([])
    const [users, setusers] = useState([])
    const [visites, setvisites] = useState([])
    const [selectedCode, setselectedCode] = useState(null)

    const traitement = (d1 = datedebut, d2 = datefin, u = userCode) => {
        setloading(true)
        getTravailVisites(d1, d2, u)
            .then((result) => {
                if (result?.data?.success === 1) {
                    setstats(result.data.stats || {})
                    setactions(result.data.actions || [])
                    setusers(result.data.users || [])
                    setvisites(result.data.visites || [])
                } else {
                    toast(t('Impossible de charger le rapport. Vérifiez la connexion au serveur.'), { type: 'error', position: 'top-right' })
                }
            })
            .catch((err) => console.log(err))
            .finally(() => setloading(false))
    }

    useEffect(() => {
        getTravailVisiteUsers().then((result) => {
            if (result?.data?.success === 1) setuserOptions(result.data.data || [])
        })
        traitement()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onSearch = () => {
        if (datedebut > datefin) {
            toast(t('La date de début doit être antérieure à la date de fin.'), { type: 'warning', position: 'top-right' })
            return
        }
        traitement(datedebut, datefin, userCode)
    }

    // ---- Calculs dérivés ----
    const tauxGlobal = pct(stats.nb_oui, stats.nb_reponses)
    const actionsBySection = useMemo(() => _.groupBy(actions, 'section'), [actions])
    const actionFaible = useMemo(
        () => (actions.length ? _.minBy(actions, (a) => pct(a.nb_oui, a.total)) : null),
        [actions]
    )
    const usersTries = useMemo(() => _.orderBy(users, (u) => pct(u.nb_oui, u.total), 'desc'), [users])

    const filteredVisites = useMemo(() => {
        const q = search.trim().toLowerCase()
        if (!q) return visites
        return visites.filter((v) =>
            `${v.nompdv || ''} ${v.pdv_code || ''} ${v.user || ''}`.toLowerCase().includes(q)
        )
    }, [visites, search])

    return (
        <div className="vrr">
            <ToastContainer />

            <div className="page-header">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item"><a href="/rapports"><i className="fe fe-life-buoy mr-1" /> {t('rapports.Rapport')}</a></li>
                    <li className="breadcrumb-item active" aria-current="page">{t('Travail effectué')}</li>
                </ol>
            </div>

            {/* ===================== EN-TÊTE + FILTRES ===================== */}
            <header className="vrr-head">
                <div>
                    <h1 className="vrr-title">{t('Travail effectué en visite')}</h1>
                    <p className="vrr-subtitle">
                        {t('Actions réalisées par les merchandisers en point de vente : réassort, entretien du rayon, casses et invendus, promotions.')}
                    </p>
                </div>

                <div className="vrr-filters" role="search">
                    <label className="vrr-field">
                        <span>{t('Du')}</span>
                        <input type="date" value={datedebut} onChange={(e) => setdatedebut(e.target.value)} />
                    </label>
                    <label className="vrr-field">
                        <span>{t('Au')}</span>
                        <input type="date" value={datefin} onChange={(e) => setdatefin(e.target.value)} />
                    </label>
                    <label className="vrr-field vrr-field-wide">
                        <span>{t('Merchandiser')}</span>
                        <select value={userCode} onChange={(e) => setuserCode(e.target.value)}>
                            <option value="all">{t('Tous les merchandisers')}</option>
                            {userOptions.map((u) => (
                                <option key={u.user_code} value={u.user_code}>{u.nom}</option>
                            ))}
                        </select>
                    </label>
                    <button type="button" className="vrr-btn" onClick={onSearch} disabled={loading}>
                        <i className="ti-search mr-1" /> {t('Afficher')}
                    </button>
                </div>
            </header>

            {loading ? (
                <div className="card"><SqaureLoader /></div>
            ) : !stats.nb_visites ? (
                <div className="vrr-empty">
                    <h2>{t('Aucun travail saisi sur cette période')}</h2>
                    <p>{t('Élargissez les dates ou choisissez « Tous les merchandisers ». Les saisies apparaissent ici après la synchronisation des téléphones.')}</p>
                </div>
            ) : (
                <>
                    {/* ===================== CHIFFRES CLÉS ===================== */}
                    <section className="vrr-kpis" aria-label={t('Chiffres clés')}>
                        <div className="vrr-kpi">
                            <strong>{stats.nb_visites}</strong>
                            <span>{t('visites renseignées')}</span>
                        </div>
                        <div className="vrr-kpi">
                            <strong>{stats.nb_pdv}</strong>
                            <span>{t('points de vente')}</span>
                        </div>
                        <div className="vrr-kpi">
                            <strong style={{ color: colorForRate(tauxGlobal) }}>
                                {tauxGlobal ?? '–'}<small>%</small>
                            </strong>
                            <span>{t('taux de réalisation global')}</span>
                        </div>
                        <div className="vrr-kpi">
                            <strong style={{ color: colorForRate(actionFaible ? pct(actionFaible.nb_oui, actionFaible.total) : null) }}>
                                {actionFaible ? pct(actionFaible.nb_oui, actionFaible.total) : '–'}<small>%</small>
                            </strong>
                            <span>
                                {t('action la moins réalisée')}
                                {actionFaible && <> : <b>{t(actionFaible.libelle_fr)}</b></>}
                            </span>
                        </div>
                    </section>

                    {/* ===================== RÉALISATION PAR ACTION ===================== */}
                    <section className="vrr-panel vrr-health">
                        <div className="vrr-panel-head">
                            <h2>{t('Taux de réalisation par action')}</h2>
                            <ul className="vrr-legend">
                                <li><i style={{ background: colorForRate(80) }} />≥ 80%</li>
                                <li><i style={{ background: colorForRate(50) }} />50 – 79%</li>
                                <li><i style={{ background: colorForRate(0) }} />&lt; 50%</li>
                            </ul>
                        </div>

                        {Object.keys(SECTION_TITLES).map((section) =>
                            actionsBySection[section] ? (
                                <div className="vrr-health-group" key={section}>
                                    <h3>{section} {t(SECTION_TITLES[section])}</h3>
                                    {actionsBySection[section].map((a) => (
                                        <RateRow key={a.cle} label={t(a.libelle_fr)} oui={a.nb_oui} total={a.total} t={t} />
                                    ))}
                                </div>
                            ) : null
                        )}
                    </section>

                    {/* ===================== PAR MERCHANDISER ===================== */}
                    <section className="vrr-panel">
                        <div className="vrr-panel-head">
                            <h2>{t('Par merchandiser')} <span className="vrr-count">{users.length}</span></h2>
                        </div>
                        {usersTries.map((u) => (
                            <RateRow
                                key={u.user_code}
                                label={u.user || u.user_code}
                                sub={`${u.nb_visites} ${t('visite(s)')}`}
                                oui={u.nb_oui}
                                total={u.total}
                                t={t}
                            />
                        ))}
                    </section>

                    {/* ===================== LISTE DES VISITES ===================== */}
                    <section className="vrr-panel">
                        <div className="vrr-panel-head">
                            <h2>{t('Détail des visites')} <span className="vrr-count">{filteredVisites.length}</span></h2>
                            <div className="vrr-table-tools">
                                <input
                                    type="search"
                                    className="vrr-search"
                                    placeholder={t('Rechercher un PDV ou un merchandiser')}
                                    value={search}
                                    onChange={(e) => setsearch(e.target.value)}
                                />
                                <ExportToExcel id="table-travail-visite" namefile="travail_effectue" t={t} />
                            </div>
                        </div>

                        <div className="vrr-table-wrap">
                            <table id="table-travail-visite" className="vrr-table">
                                <thead>
                                    <tr>
                                        <th>{t('Date')}</th>
                                        <th>{t('Merchandiser')}</th>
                                        <th>{t('Point de vente')}</th>
                                        <th className="vrr-center">{t('Durée')}</th>
                                        <th className="vrr-center">{t('Actions réalisées')}</th>
                                        <th><span className="sr-only">{t('Actions')}</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredVisites.map((v) => {
                                        const p = pct(v.nb_oui, v.nb_total)
                                        return (
                                            <tr key={v.code} onClick={() => setselectedCode(v.code)} className="vrr-row">
                                                <td className="vrr-nowrap">
                                                    <b>{moment(v.date_visite).format('DD/MM/YYYY')}</b>
                                                    <div className="vrr-muted">
                                                        {v.heure_debut || ''}{v.heure_fin ? ` – ${v.heure_fin}` : ''}
                                                    </div>
                                                </td>
                                                <td>{v.user || v.user_code}</td>
                                                <td>
                                                    <b>{v.nompdv || v.pdv_code}</b>
                                                    {v.nompdv && <div className="vrr-muted">{v.pdv_code}</div>}
                                                </td>
                                                <td className="vrr-center">{v.duree !== null && v.duree >= 0 ? `${v.duree} min` : '–'}</td>
                                                <td className="vrr-center">
                                                    <span className="vrr-note" style={{ '--note-color': colorForRate(p) }} title={formatPct(p)}>
                                                        {v.nb_oui}/{v.nb_total}
                                                    </span>
                                                </td>
                                                <td className="vrr-right">
                                                    <button
                                                        type="button"
                                                        className="vrr-link"
                                                        onClick={(e) => { e.stopPropagation(); setselectedCode(v.code) }}
                                                    >
                                                        {t('Ouvrir')}
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                    {filteredVisites.length === 0 && (
                                        <tr><td colSpan={6} className="vrr-center vrr-muted">{t('Aucune visite ne correspond à la recherche.')}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </>
            )}

            {selectedCode && (
                <TravailVisiteDetail code={selectedCode} t={t} onClose={() => setselectedCode(null)} />
            )}
        </div>
    )
}

/* ---------------------------------------------------------------------------------- */

/** Une ligne : libellé, barre de réalisation, pourcentage, Oui / total. */
function RateRow({ label, sub, oui, total, t }) {
    const p = pct(oui, total)
    const color = colorForRate(p)
    return (
        <div className="trv-rate-row">
            <span className="trv-rate-label">
                {label}
                {sub && <span className="vrr-muted"> · {sub}</span>}
            </span>
            <div className="trv-rate-bar" role="img" aria-label={`${label} : ${formatPct(p)}`}>
                <span style={{ width: `${p || 0}%`, background: color }} />
            </div>
            <span className="trv-rate-pct" style={{ color }}>{formatPct(p)}</span>
            <span className="trv-rate-n" title={t('Oui / réponses')}>{oui}/{total}</span>
        </div>
    )
}
